import type { WhenReport } from '../../types/reports';
import type { Segment } from '../reportsView';
import { formatMoney } from '../ownerOverview';
import { hourLabel, intLabel, moneyLabel, NO_COMPARISON_NOTE, plural, strong, text, UNKNOWN, upperFirst, variationText, windowLabel, type BarView, type SectionView, type StatView } from './shared';

/**
 * P-084 · R4 «¿Cuándo vendo más?» — presentación pura. Responde primero (la frase), después 2–4 cifras, la
 * distribución por día de la semana y por hora (dibujo con su texto), y el detalle (hora por hora, los días con
 * más ventas). Lo que no se puede afirmar lo dice el servidor (`enough.reasons`); aquí solo se escribe.
 */
export interface WhenView {
  say: Segment[];
  /** El patrón no se afirma: las razones del servidor, para decirlas bajo la frase. */
  caution: string[];
  stats: StatView[];
  weekdays: { items: BarView[]; caption: string } | null;
  hours: { items: BarView[]; caption: string } | null;
  comparison: string | null;
  detail: SectionView[];
  notes: string[];
  empty: { title: string; description: string } | null;
}

export const WHEN_EMPTY_TITLE = 'No hubo ventas en este periodo.';
export const WHEN_EMPTY_TEXT = 'Cuando se registren, aquí verás tus días y horas fuertes.';
export const WHEN_INSUFFICIENT = 'Todavía no hay suficientes ventas en este periodo para detectar un patrón.';
export const WHEN_OPERATIONS_NOTE = 'Operaciones cuenta ventas cobradas; las anuladas no cuentan y las devoluciones restan dinero.';
export const WHEN_PRUDENCE_NOTE = 'Los mínimos para afirmar un patrón son de prudencia, no una garantía estadística.';

export function whenReportView(report: WhenReport): WhenView {
  const { totals, enough, strongestWeekday, window } = report;
  const notes = [WHEN_OPERATIONS_NOTE];
  if (totals.operations === 0) {
    return { say: [text(WHEN_EMPTY_TITLE)], caution: [], stats: statsOf(report), weekdays: null, hours: null, comparison: null, detail: [], notes, empty: { title: WHEN_EMPTY_TITLE, description: WHEN_EMPTY_TEXT } };
  }

  const dayWinner = strongestWeekday.byOperations;
  const franja = window.byOperations;
  const say: Segment[] = [];
  if (enough.weekday && dayWinner && enough.hour && franja) {
    const dayText = dayWinner.tiedWith.length > 0 ? `${[dayWinner.label, ...dayWinner.tiedWith].join(' y ')} (empatados)` : pluralDay(dayWinner.label);
    say.push(text('Tus ventas se concentran los '), strong(dayText), text(', '), strong(windowLabel(franja.startHour, franja.endHour)), text('.'));
  } else if (enough.weekday && dayWinner) {
    say.push(text('Tu día fuerte es el '), strong(dayWinner.tiedWith.length > 0 ? `${[dayWinner.label, ...dayWinner.tiedWith].join(' y ')} (empate)` : dayWinner.label), text('.'));
  } else if (enough.hour && franja) {
    say.push(text(totals.daysInPeriod === 1 ? 'Vendiste más ' : 'Tu franja fuerte es '), strong(windowLabel(franja.startHour, franja.endHour)), text('.'));
  } else {
    say.push(text(WHEN_INSUFFICIENT));
  }
  if (enough.weekday && dayWinner && strongestWeekday.byRevenue && strongestWeekday.byRevenue.weekday !== dayWinner.weekday && !dayWinner.tiedWith.includes(strongestWeekday.byRevenue.label)) {
    say.push(text(' Por dinero, tu mejor día es el '), strong(strongestWeekday.byRevenue.label), text('.'));
  }
  const caution = enough.weekday && enough.hour ? [] : enough.reasons;
  if (!enough.weekday || !enough.hour) notes.push(WHEN_PRUDENCE_NOTE);

  // Barras por día de la semana: operaciones por día de calendario (el servidor ya dividió), con el dinero en el texto.
  const weekdayMax = Math.max(1, ...report.weekdays.map((day) => day.averageOperations ?? 0));
  const weekdays: BarView[] = report.weekdays.map((day) => ({
    key: `d${day.weekday}`,
    label: upperFirst(day.label),
    valueLabel: day.exposure === 0
      ? 'no cae en el periodo'
      : `${intLabel(day.operations)} ${plural(day.operations, 'operación', 'operaciones')}${day.exposure > 1 ? ` (${intLabel(day.averageOperations ?? 0)} por día)` : ''} · ${report.meta.currency === null ? UNKNOWN : formatMoney(day.revenue)}`,
    percent: Math.round(((day.averageOperations ?? 0) / weekdayMax) * 100),
    highlighted: Boolean(dayWinner) && enough.weekday && (dayWinner!.weekday === day.weekday || dayWinner!.tiedWith.includes(day.label)),
  }));
  const hourMax = Math.max(1, ...report.hours.map((hour) => hour.operations));
  const hours: BarView[] = report.hours
    .filter((hour) => hour.operations > 0 || Number(hour.revenue) !== 0)
    .map((hour) => ({
      key: `h${hour.hour}`,
      label: hourLabel(hour.hour),
      valueLabel: `${intLabel(hour.operations)} ${plural(hour.operations, 'operación', 'operaciones')} · ${report.meta.currency === null ? UNKNOWN : formatMoney(hour.revenue)}`,
      percent: Math.round((hour.operations / hourMax) * 100),
      highlighted: Boolean(franja) && enough.hour && hour.hour >= franja!.startHour && hour.hour < franja!.endHour,
    }));

  const comparison = report.previous
    ? `Contra el periodo anterior: ${variationText(report.previous.variation.operations, 'percent-count').text} en operaciones${report.previous.variation.revenue ? ` y ${variationText(report.previous.variation.revenue, 'percent').text} en dinero` : ''}.`
    : report.meta.history.canCompare ? null : NO_COMPARISON_NOTE;

  const detail: SectionView[] = [
    {
      key: 'days',
      title: 'Los días con más ventas',
      rows: report.topDays.map((day) => ({
        key: day.day, title: upperFirst(`${day.label} ${day.day.slice(8, 10)}`), subtitle: null, meta: [],
        value: `${intLabel(day.operations)} ${plural(day.operations, 'operación', 'operaciones')}`, valueTone: 'default', badge: null,
        figures: [{ label: 'Entró', value: report.meta.currency === null ? UNKNOWN : formatMoney(day.revenue) }], destination: null,
      })),
      empty: null, note: null, more: null,
    },
  ];

  return {
    say,
    caution,
    stats: statsOf(report),
    weekdays: totals.daysInPeriod >= 7 ? { items: weekdays, caption: 'Operaciones por día de la semana (promedio por día de calendario cuando un día cae varias veces).' } : null,
    hours: hours.length > 0 ? { items: hours, caption: franja && enough.hour ? `Operaciones por hora; la franja fuerte (${windowLabel(franja.startHour, franja.endHour)}) va marcada.` : 'Operaciones por hora local.' } : null,
    comparison,
    detail,
    notes,
    empty: null,
  };
}

function statsOf(report: WhenReport): StatView[] {
  const { totals, strongestWeekday, window, enough, previous } = report;
  const franja = window.byOperations;
  return [
    { key: 'operations', label: 'Operaciones', value: intLabel(totals.operations), hint: previous ? `vs. ${intLabel(previous.operations)} antes` : totals.refunds > 0 ? `${intLabel(totals.refunds)} ${plural(totals.refunds, 'devolución', 'devoluciones')}` : null, tone: 'default' },
    { key: 'revenue', label: 'Entró', value: moneyLabel(totals.revenue), hint: previous?.revenue ? `vs. ${formatMoney(previous.revenue)} antes` : null, tone: 'default' },
    { key: 'weekday', label: 'Día fuerte', value: enough.weekday && strongestWeekday.byOperations ? upperFirst(strongestWeekday.byOperations.label) : UNKNOWN, hint: enough.weekday && strongestWeekday.byOperations ? `${Math.round(strongestWeekday.byOperations.share)} % de las operaciones` : 'sin patrón todavía', tone: enough.weekday ? 'default' : 'muted' },
    { key: 'window', label: 'Franja fuerte', value: enough.hour && franja ? windowLabel(franja.startHour, franja.endHour) : UNKNOWN, hint: enough.hour && franja ? `${Math.round(franja.share)} % de las operaciones` : 'sin patrón todavía', tone: enough.hour ? 'default' : 'muted' },
  ];
}

function pluralDay(label: string): string {
  // «los viernes», «los sábados», «los lunes» (lunes/martes/miércoles/jueves/viernes no cambian).
  return /s$/.test(label) ? label : `${label}s`;
}
